import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import { getPostLoginPath } from "../config/featureFlags";

export default function OutlookOAuthSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { updateUser } = useAuth();
  const [status, setStatus] = useState("processing");
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleSuccess = async () => {
      try {
        console.log("🔄 Processing Outlook OAuth success callback...");

        // Get encoded data from URL
        const encodedData = searchParams.get("data");
        if (!encodedData) {
          throw new Error("No authentication data received");
        }

        // Decode the data
        let tokenData;
        try {
          const decodedData = decodeURIComponent(encodedData);
          // Use browser's atob for base64 decoding (instead of Node.js Buffer)
          const jsonData = atob(decodedData);
          tokenData = JSON.parse(jsonData);
        } catch (parseError) {
          console.error("❌ Failed to parse token data:", parseError);
          throw new Error("Invalid authentication data format");
        }

        const { token, user: userData, isNewUser } = tokenData;

        if (!token || !userData) {
          throw new Error("Invalid authentication data");
        }

        console.log("✅ Token data received:", {
          email: userData.email,
          isNewUser: !!isNewUser,
          userId: userData.id,
        });

        // Store token and user data
        console.log("💾 Storing authentication data...");
        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(userData));
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        console.log("✅ Data stored in localStorage");

        // Update auth context
        console.log("🔄 Updating AuthContext...");
        const updatedUser = updateUser({ ...userData, token });

        console.log("✅ AuthContext updated:", {
          user: updatedUser,
          email: updatedUser?.email,
          role: updatedUser?.role,
        });

        setStatus("success");

        // Check for pending leave action
        const pendingAction = localStorage.getItem("pendingLeaveAction");
        let redirectPath = getPostLoginPath(userData);

        if (pendingAction) {
          try {
            const actionData = JSON.parse(pendingAction);
            if (actionData.type === "leave_action") {
              redirectPath = `/leave-action?leaveId=${actionData.leaveId}&action=${actionData.action}`;
              console.log(
                "🔄 Redirecting to pending leave action:",
                redirectPath
              );
            }
          } catch (err) {
            console.error("Failed to parse pending action:", err);
          }
        }

        // Redirect after a short delay
        setTimeout(() => {
          console.log("🚀 Redirecting to:", redirectPath);
          window.location.href = redirectPath;
        }, 1500);
      } catch (err) {
        console.error("❌ OAuth success callback error:", err);
        setError(err.message || "Authentication failed");
        setStatus("error");

        // Redirect to login after showing error
        setTimeout(() => {
          navigate("/login", { replace: true });
        }, 3000);
      }
    };

    handleSuccess();
  }, [searchParams, navigate, updateUser]);

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gray-50">
      <motion.div
        className="text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {status === "processing" && (
          <>
            <div className="mb-6">
              <svg
                className="animate-spin h-16 w-16 text-primary mx-auto"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Completing authentication...
            </h2>
            <p className="text-gray-600">Please wait while we sign you in.</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="mb-6">
              <svg
                className="h-16 w-16 text-green-500 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Authentication Successful!
            </h2>
            <p className="text-gray-600">Redirecting to dashboard...</p>
          </>
        )}

        {status === "error" && (
          <>
            <div className="mb-6">
              <svg
                className="h-16 w-16 text-red-500 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-red-600 mb-2">
              Authentication Failed
            </h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-500">
              Redirecting to login page...
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}
